"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";

const LORE_MESSAGES = [
  "Consulting ancient arcane grimoires...",
  "Weaving mythical name syllables...",
  "Channeling forgotten realm chronicles...",
  "Awakening legendary hero lineages...",
  "Summoning celestial inspirations...",
];

interface LoadingScreenProps {
  fullScreen?: boolean;
  message?: string;
}

export default function LoadingScreen({
  fullScreen = true,
  message,
}: LoadingScreenProps) {
  const [loreIndex, setLoreIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setLoreIndex((prev) => (prev + 1) % LORE_MESSAGES.length);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      role="status"
      aria-label="Loading..."
      className={`relative flex flex-col items-center justify-center overflow-hidden bg-background/95 backdrop-blur-xl ${
        fullScreen
          ? "fixed inset-0 z-50 min-h-screen w-screen px-4"
          : "min-h-[420px] w-full p-8 rounded-3xl border border-border/60"
      }`}
    >
      {/* Mystical Atmospheric Background Orbs */}
      <div className="absolute inset-0 pointer-events-none -z-10 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.25, 1],
            opacity: [0.15, 0.3, 0.15],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] sm:w-[460px] h-[340px] sm:h-[460px] rounded-full bg-gradient-to-tr from-primary/30 via-amber-500/20 to-yellow-600/10 blur-3xl"
        />
      </div>

      {/* Centerpiece: Official Name Arcane Brand Logo */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative flex flex-col items-center justify-center mb-6"
      >
        {/* Subtle Ambient Glow Behind Logo */}
        <motion.div
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: [0.98, 1.05, 0.98],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute w-48 sm:w-64 h-16 rounded-full bg-primary/20 blur-xl pointer-events-none"
        />

        {/* Name Arcane Official Brand Logo */}
        <div className="relative z-10 flex items-center justify-center">
          <Image
            src="/images/Logo/logo-light.png"
            alt="Name Arcane"
            width={280}
            height={48}
            className="h-10 sm:h-12 w-auto object-contain dark:hidden drop-shadow-md"
            priority
          />
          <Image
            src="/images/Logo/logo-dark.png"
            alt="Name Arcane"
            width={280}
            height={48}
            className="h-10 sm:h-12 w-auto object-contain hidden dark:block drop-shadow-lg"
            priority
          />
        </div>

        {/* Tagline */}
        <motion.span
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="text-xs sm:text-sm italic font-serif text-amber-600/90 dark:text-amber-400/90 tracking-widest mt-2"
        >
          every name has a spell in it
        </motion.span>
      </motion.div>

      {/* Shimmering Linear Progress Bar */}
      <div className="relative w-48 sm:w-64 h-1.5 rounded-full overflow-hidden bg-muted/60 mb-5 border border-border/40">
        <motion.div
          animate={{
            x: ["-100%", "100%"],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="h-full w-2/3 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent"
        />
      </div>

      {/* Rotating Lore Status Message */}
      <div className="h-6 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={message || loreIndex}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3 }}
            className="text-xs sm:text-sm text-muted-foreground font-medium text-center"
          >
            {message || LORE_MESSAGES[loreIndex]}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}
